import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'

import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { PlaylistTag } from 'types/playlist'
import type { PopoverItemType } from 'types/ui'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  deletePlaylistTag,
  editPlaylistTag,
} from '@disco/state-management/actions/PlaylistTagActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import type { featureData } from '@disco/state-management/selectors/business'
import { useFeatureData } from '@disco/state-management/selectors/business'
import { getPlaylistTags } from '@disco/state-management/selectors/tags'
import ColorIndicatorsAlert from 'components/common/color-indicators-alert'
import DocumentHeadTags from 'components/document-head-tags'
import { Flex } from 'components/ds'
import Button from 'components/ui/button'
import DataTable from 'components/ui/data-table'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import Input from 'components/ui/input'
import ModalDialog from 'components/ui/modal-dialog'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import PricingBadge from 'components/ui/pricing-badge'
import Tag from 'components/ui/tag'
import PlaylistTagColorIndicatorModal from './PlaylistTagColorIndicatorModal'
import PlaylistTagEditModal from './PlaylistTagEditModal'

const PlaylistTagsSettings = (): React.ReactNode => {
  const dispatch = useDispatch()
  const playlistTags: PlaylistTag[] = useSelector(getPlaylistTags)
  const isPhone = useSelector(isDeviceTypePhone)
  const {
    badgeTitle: colorIndicatorsBadgeTitle,
    isFeatureEnabled: colorIndicatorsEnabled,
  }: featureData = useFeatureData(COLOR_INDICATORS)
  const [modal, setModal] = useState<React.ReactNode>(null)

  const closeModal = () => setModal(null)

  const [query, setQuery] = useState<string>('')

  const updateSearchQuery = (event: React.ChangeEvent<HTMLInputElement>) =>
    setQuery(event.target.value)

  useEffect(() => {
    dispatch(sendMixpanelEvent('Open playlist tags settings'))
  }, [])

  const getPricingBadge = (): React.ReactNode | null | undefined =>
    !colorIndicatorsEnabled ? (
      <PricingBadge
        title={colorIndicatorsBadgeTitle}
        intercomMsg="I’m interested in subscribing to Pro to access tag color indicators"
      />
    ) : undefined

  const getMenuOptions = (tag: PlaylistTag) => {
    let options: PopoverItemType[] = [
      {
        label: 'Edit',
        onClick: () =>
          setModal(
            <PlaylistTagEditModal
              editingTag={tag}
              onRequestClose={closeModal}
            />,
          ),
      },
    ]

    if (tag.color) {
      options = [
        ...options,
        {
          badge: getPricingBadge(),
          disabled: !colorIndicatorsEnabled,
          label: 'Remove color indicator',
          onClick: () => dispatch(editPlaylistTag({ ...tag, color: null })),
        },
      ]
    } else {
      options = [
        ...options,
        {
          badge: getPricingBadge(),
          disabled: !colorIndicatorsEnabled,
          label: 'Add color indicator',
          onClick: () =>
            setModal(
              <PlaylistTagColorIndicatorModal
                tag={tag}
                onClose={closeModal}
                onSubmit={async color => {
                  try {
                    await dispatch(editPlaylistTag({ ...tag, color }))
                    closeModal()
                  } catch (error) {}
                }}
              />,
            ),
        },
      ]
    }

    options = [
      ...options,
      {
        label: 'Delete',
        destructive: true,
        onClick: () =>
          setModal(
            <ModalDialog
              title="Delete playlist tag"
              primaryAction={{
                caption: 'Delete',
                onClick: async () => {
                  try {
                    await dispatch(deletePlaylistTag(tag))
                    closeModal()
                  } catch (error) {}
                },
              }}
              status="danger"
              onCancel={closeModal}
            >
              Are you sure you want to delete “{tag.name}”? This can’t be
              undone.
            </ModalDialog>,
          ),
      },
    ]
    return options
  }

  return (
    <>
      <DocumentHeadTags title="Settings | Playlist Tags | DISCO" />
      <PageHeader title="Manage Playlist Tags" />
      <PageContainer>
        <Flex gap="2" direction={isPhone ? 'column' : 'row'} pb="2">
          <div className="TrackTagsSettings_SearchInput">
            <Input
              icon="search"
              placeholder="Search"
              value={query}
              onChange={updateSearchQuery}
            />
          </div>
          <div className="TrackTagsSettings_SearchBtn">
            <Button
              caption="Add playlist tags"
              icon="add-tag"
              onClick={() =>
                setModal(<PlaylistTagEditModal onRequestClose={closeModal} />)
              }
            />
          </div>
        </Flex>
        <ColorIndicatorsAlert />
        <DataTable
          appearance="plain"
          columns={[
            {
              name: 'Name',
              toSortable: (tag: PlaylistTag) => tag.name,
              render: (tag: PlaylistTag) => (
                <Tag
                  tag={tag.name}
                  tagColor={colorIndicatorsEnabled ? tag.color : undefined}
                />
              ),
            },
            {
              contentType: 'button',
              name: 'Actions',
              width: 'content',
              render: (tag: PlaylistTag) => (
                <EllipsisMenu
                  compact
                  hasArrow={false}
                  icon="ellipsis"
                  options={getMenuOptions(tag)}
                />
              ),
            },
          ]}
          data={playlistTags
            .map((tag: PlaylistTag) => ({ ...tag, key: tag.id }))
            .filter(
              // TODO: unify this with other inline search
              (tag: PlaylistTag) =>
                !query || tag.name.toLowerCase().includes(query.toLowerCase()),
            )}
          emptyState={
            query
              ? 'Sorry! That tag doesn’t exist. Create a new playlist tag above.'
              : 'No playlist tags here yet!'
          }
        />
        {modal}
      </PageContainer>
    </>
  )
}

export default PlaylistTagsSettings
